import type { Document, DocumentUploadResponse2, FilterDocumentsResponse } from '@api/domain/types';
import type { StorageAccessResponse } from '@api/domain/types/blobStorage';
import { SignalREventResponse } from '@interfaces/base';
import { RetrieveReferencedDocumentsParams } from '@interfaces/methods';
import { RemoveImageDocumentPayload, RotateImageDocumentPayload } from '@reducers/v2/imageHandlingV2/thunks';
import { DOC_TYPE, DOCUMENT_ENTITY_TYPE, INDIRECT_LINKING_TEXT, TAG_KEYS } from '@components/imageHandling/common';

export type DocType = (typeof DOC_TYPE)[keyof typeof DOC_TYPE];

// === Domain-derived types (from Document via getDocumentById) ===

export type PageDetails = NonNullable<Document['pageDetails']>[number];
export type ODataDocumentCropSettings = NonNullable<Document['cropSettings']>[number];
export type ODataDocumentLink = NonNullable<Document['links']>[number];
export type ODataDocument = Document;

// === Domain-derived types (from FilterDocumentsResponse via retrieveReferencedDocuments) ===

export type RetrievedDocumentReference = NonNullable<FilterDocumentsResponse['documents']>[number];
export type RetrievedDocumentReferenceCropSettings = NonNullable<RetrievedDocumentReference['cropSettings']>[number];
export type RetrievedDocumentReferenceLink = NonNullable<RetrievedDocumentReference['links']>[number];

// === Common types (collapsed — both resolve to same Document source) ===

export type DocumentCropSettings = RetrievedDocumentReferenceCropSettings | ODataDocumentCropSettings;
export type ODataOrRetrievedDocumentReference = ODataDocument | RetrievedDocumentReference;
export type ODataOrRetrievedDocumentLink = ODataDocumentLink | RetrievedDocumentReferenceLink;

// === Store item types ===

interface ImageDocumentStoreItemAdditionalFields {
  customRotate: { [key: number]: number };
  documentTag: DocType;
  isPDF: boolean;
  isDocument: boolean;
  id: number;
  widthOriginal: number;
  heightOriginal: number;
  activeImageWidth: number;
  downloadUrl: string;
  pagesList: Record<number, any>;
  url?: string;
  urlZoom?: string;
  urlSmall?: string;
  originUrl3x?: string;
  originUrl?: string;
  scopeId?: number;
  scopeName?: string;
  base64?: string;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  isProcessing?: boolean;
  croppedImageUri?: string;
}

export interface ImageDocumentStoreItemBasedOnODataDocument extends ODataDocument, ImageDocumentStoreItemAdditionalFields {}

export interface ImageDocumentStoreItemBasedOnRetrievedDocumentReference extends RetrievedDocumentReference, ImageDocumentStoreItemAdditionalFields {}

export type ImageDocumentStoreItem = ImageDocumentStoreItemBasedOnODataDocument | ImageDocumentStoreItemBasedOnRetrievedDocumentReference;

export type ImageDocumentStoreItemWithAngle = ImageDocumentStoreItem & { angle: number };

// === Other types ===

export type BlobAccessInfo = StorageAccessResponse;

export interface DocumentCategories {
  loaded: boolean;
  items: { name: string; title: string; value: string }[];
}

export interface ImageDocumentReference {
  scopeId: number;
  scopeName: string;
  scopeReferenceId: number;
  scopeReferenceName: string;
}

export interface ImageLinkForAdding {
  scopeId: number;
  scopeReferenceId: number;
  isFavorite?: boolean;
}

export interface ImageContextForAdding {
  tags?: ImageLinkForAdding[];
  documentType?: DocType;
}

export interface CreateImagePayload {}

export interface CropParams {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FetchImagesDocumentsParams extends Pick<RetrieveReferencedDocumentsParams, 'onRetrieveFinished' | 'retrieveAllDocuments' | 'storeMode'> {
  scopeId: keyof typeof TAG_KEYS;
  scopeReferenceId: number;
}

// === Action types ===

export type LoadImagesDocumentsAction = () => Promise<void>;
export type AddImageDocumentAction = (payload: CreateImagePayload) => Promise<void>;
export type RemoveImageDocumentAction = (payload: RemoveImageDocumentPayload) => Promise<void>;
export type RotateImageDocumentAction = (params: RotateImageDocumentPayload) => Promise<void>;
export type CropImageDocumentAction = (imageDocument: ImageDocumentStoreItem) => Promise<void>;

export enum PageChangeActionType {
  PREV = 'prev',
  NEXT = 'next',
}

export interface ChangePdfImageParams {
  contextKey: string;
  contentHash: string;
  documentId: number;
  pageChangeActionType: PageChangeActionType;
}

export type ImageContext =
  | {
      documentId: number;
    }
  | FetchImagesDocumentsParams
  | {};

export enum ImageHandlingDisplayMode {
  PICTURE = 'picture',
  THUMBNAIL = 'thumbnail',
}

export interface UpdateDocumentMetadataResponse {
  documentId: number;
  documentLinks: RetrievedDocumentReferenceLink[];
  documentTags: string[];
  newFileName?: string;
}

export interface UpdateDocumentMetadataSignalRResponse extends SignalREventResponse {
  contentHash: string;
  documentId: number;
  fileName: string;
  fileNameOverride: string | null;
  links: RetrievedDocumentReferenceLink[];
  tags: DocType[];
}

export interface RemoveImageDocumentSignalRResponse extends SignalREventResponse {
  documentId: number;
}

export interface RotateImageDocumentSignalRResponse extends SignalREventResponse {
  cropSettings: ODataDocumentCropSettings | null;
  documentId: number;
  isCroppedVersion: boolean;
  pageDetails: PageDetails;
}

export interface CropImageDocumentSignalRResponse extends SignalREventResponse, Pick<RotateImageDocumentSignalRResponse, 'documentId'> {
  cropSettings: ODataDocumentCropSettings;
}

export interface UploadImageDocumentSignalRResponse extends SignalREventResponse {
  documentBlobId: string;
  documentId?: number;
  image?: ODataDocument;
}

export type RequestForDocumentUploadResponse = DocumentUploadResponse2;

export type DocumentsReferencesObjectScopeName =
  | typeof INDIRECT_LINKING_TEXT
  | 'Part'
  | 'Recipe'
  | 'RecipeOperation'
  | 'NcFile'
  | 'ProductionOrder'
  | 'ProductionOperation'
  | 'Batch'
  | 'Tool';

export type ReferencesObject = Record<DocumentsReferencesObjectScopeName, ODataOrRetrievedDocumentReference[]>;

export interface ImageUploadImageCropSettingsPayload extends RetrievedDocumentReferenceCropSettings {}

export type GroupedImageItem = ImageDocumentStoreItem & {
  index: number;
  imageLinkKey: string;
  isFavorite: boolean;
};

export interface GroupedImage {
  link: ODataOrRetrievedDocumentLink;
  images: GroupedImageItem[];
  scopeId: number;
  key: string;
}

export interface GenerateGropedImagesAndKeysResult {
  groupedImages: Record<string, GroupedImage>;
  groupedImagesKeys: string[];
}

export interface IsSomeLinkFavoriteForScopeParams {
  links: ODataOrRetrievedDocumentLink[] | null | undefined;
  scopeId: number | null | undefined;
  scopeReferenceId: number | null | undefined;
}
